import { useEffect, useMemo, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import {
    Autocomplete,
    Chip,
    CircularProgress,
    IconButton,
    InputLabel,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { InfoCircleOutlined } from '@ant-design/icons';
import RequiredAsterisk from 'components/RequiredAsterisk';
import UserGroupDto from 'common/dto-types/user-group-dto';
import axiosServices from 'utils/axios';
import { MessageFormFields } from './dto';
import { MessageType } from 'common/dto-types/message-dto';

type MessageGroupSelectionProps = {
    name: string;
    label: string;
    initialValues?: UserGroupDto[];
    restrictedGroups?: UserGroupDto[];
    isRequired?: boolean;
    disabled?: boolean;
};

const MessageGroupSelection = ({
    name,
    label,
    initialValues,
    restrictedGroups,
    isRequired = false,
    disabled = false,
}: MessageGroupSelectionProps) => {
    const [field, meta, helpers] = useField({ name });
    const { values } = useFormikContext<MessageFormFields>();

    const [serverOptions, setServerOptions] = useState<UserGroupDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [value, setValue] = useState<UserGroupDto[]>(initialValues || []);
    const previousRestrictedModeRef = useRef<boolean>(false);

    const isRestrictedMode = useMemo(() => {
        const isArticleType = values.messageType === MessageType.Article;
        const hasRestrictedGroups = (restrictedGroups && restrictedGroups.length > 0) ?? false;
        return isArticleType && hasRestrictedGroups;
    }, [values.messageType, restrictedGroups]);

    const options = isRestrictedMode ? restrictedGroups || [] : serverOptions;

    // Server-side fetch (only in unrestricted mode)
    useEffect(() => {
        if (isRestrictedMode) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const timeout = setTimeout(() => {
            const queryParam = inputValue ? `?andSpecifications=NameSpecification:${inputValue}` : '';
            axiosServices
                .get(`userGroups${queryParam}`)
                .then((res) => setServerOptions(res.data.elements))
                .finally(() => setLoading(false));
        }, 500);

        return () => clearTimeout(timeout);
    }, [inputValue, isRestrictedMode]);

    // Sync value with Formik field value
    useEffect(() => {
        if (field.value && field.value !== value) {
            setValue(field.value);
        }
    }, [field.value, value]);

    // Clear selections when switching between restricted/unrestricted mode
    useEffect(() => {
        if (disabled) {
            return;
        }

        // Only clear when the mode actually changes (not on every render)
        if (previousRestrictedModeRef.current !== isRestrictedMode) {
            previousRestrictedModeRef.current = isRestrictedMode;

            if (isRestrictedMode && restrictedGroups && restrictedGroups.length > 0) {
                // In restricted mode: keep only groups that exist in the restricted list
                const validGroups = value.filter((selectedGroup) =>
                    restrictedGroups.some((restrictedGroup) => restrictedGroup.id === selectedGroup.id),
                );
                setValue(validGroups);
                helpers.setValue(validGroups, false);
            } else {
                // Switching to unrestricted mode or no restricted groups: clear all
                setValue([]);
                helpers.setValue([], false);
            }
        }
    }, [isRestrictedMode, disabled, restrictedGroups, value, helpers]);

    const hasError = Boolean(meta.touched && meta.error);

    return (
        <Stack spacing={1.25}>
            {/* Label with optional info icon */}
            <Stack direction="row" spacing={0.5} alignItems="center">
                <InputLabel htmlFor={name}>
                    {label}
                    {isRequired && <RequiredAsterisk />}
                </InputLabel>
                {isRestrictedMode && !disabled && (
                    <Tooltip
                        title={
                            <Typography variant="body2">
                                Only these groups can view this article content due to restriction from Contentful and
                                availability in Media Hub
                            </Typography>
                        }
                        arrow
                        placement="right"
                    >
                        <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                            <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* Read-only mode: Show simple chips without Autocomplete */}
            {disabled && value.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {value.map((group) => (
                        <Chip key={group.id} label={group.name} size="small" color="primary" variant="outlined" />
                    ))}
                </Stack>
            )}
            {disabled && value.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No groups selected
                </Typography>
            )}
            {!disabled && (
                /* Editable mode: Show Autocomplete */
                <Autocomplete
                    id={name}
                    multiple
                    options={options}
                    value={value}
                    disabled={disabled}
                    loading={loading}
                    popupIcon={loading ? <CircularProgress size={20} /> : undefined}
                    filterOptions={(x) => x}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    onChange={(_, newValue) => {
                        setValue(newValue);
                        helpers.setValue(newValue, true);
                    }}
                    onInputChange={(_, newInputValue) => {
                        if (!isRestrictedMode) {
                            setInputValue(newInputValue);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} error={hasError} helperText={hasError ? meta.error : ''} />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                        ))
                    }
                />
            )}
        </Stack>
    );
};

export default MessageGroupSelection;
